import { QueryCache, QueryClient, QueryFunction } from "@tanstack/react-query";
import { captureException } from "@sentry/react";

/** CSR-1989 — carries the HTTP status alongside the message so callers, and the
 *  retry policy below, can tell a transient server failure from a client error.
 *  A plain `Error` forced every consumer to re-parse the status out of the
 *  message, so in practice nobody did and the status was lost. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new ApiError(res.status, `${res.status}: ${text}`);
  }
}

// TanStack calls `retry` with a 0-based `failureCount` (0 when deciding the first
// retry), so this bound is a retry count: 2 allows the original request plus two
// retries, three attempts in total. Confirmed by counting requests in the browser
// against a failing endpoint.
const MAX_QUERY_RETRIES = 2;
const RETRYABLE_CLIENT_STATUSES = [408, 429];

/** CSR-1989 — queries are GETs, so a failed one is safe to repeat. Retrying
 *  matters most on the `refetchOnWindowFocus` path: a user returning to an idle
 *  tab hits a single refetch, and without a retry one transient failure left the
 *  page dead until a full reload. A 4xx is not retried — repeating the same
 *  request cannot fix an auth or not-found error, and doing so only delays the
 *  error state. Mutations keep `retry: false`; they are not idempotent. */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_QUERY_RETRIES) return false;
  if (error instanceof ApiError) {
    if (RETRYABLE_CLIENT_STATUSES.includes(error.status)) return true;
    return error.status >= 500;
  }
  // `fetch` rejects with a TypeError when the request never reached a server (a
  // network drop or DNS failure) — transient, so worth repeating. Anything else
  // is not retried on purpose: most query hooks in api.ts still signal a non-OK
  // response with a plain `Error` that carries no status, and retrying those
  // would silently repeat 4xx responses app-wide. Migrating a hook to
  // `throwIfResNotOk` is what opts it into status-aware retries.
  return error instanceof TypeError;
}

/** CSR-1989 — the first element of every query key is a static scope literal
 *  ("insureds", "policies", …). Only that is safe to report: the rest of the key
 *  holds parameters, and for search queries one of them is the term the user
 *  typed — a customer name, DOT number, email or phone. `queryHash` is not a
 *  substitute; TanStack builds it by serializing the key, so it carries the same
 *  values verbatim. */
function queryScopeOf(queryKey: readonly unknown[]): string {
  return typeof queryKey[0] === "string" ? queryKey[0] : "unknown";
}

/** CSR-1989 — query failures were invisible: no UI error, no log, no Sentry
 *  event. Reporting them centrally means the next occurrence arrives with its
 *  status code and the failing screen instead of a user having to describe it. */
const queryCache = new QueryCache({
  onError: (error, query) => {
    captureException(error, {
      extra: {
        queryScope: queryScopeOf(query.queryKey),
        status: error instanceof ApiError ? error.status : undefined,
      },
    });
  },
});

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const res = await apiFetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  queryCache,
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      // CSR-1638 — keep AMS in sync with backend data without a manual browser
      // reload. Cached answers are fresh for 60s, then refetch on the next mount
      // or when the user returns to the tab / reconnects. No always-on polling
      // (refetchInterval stays off) to keep the DC-served reads proportionate.
      // The manual "Refresh Data" flow (CSR-1086) is unaffected: its polling
      // status hooks set their own staleTime/refetchInterval and invalidate
      // explicitly, overriding these defaults.
      refetchInterval: false,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      staleTime: 60_000,
      retry: shouldRetryQuery,
    },
    mutations: {
      retry: false,
    },
  },
});

/** CSR-2309 — shared so the 401 seam can retract the signed-in user without
 *  importing the hook that reads it. */
export const AUTH_USER_QUERY_KEY = ["/api/auth/user"] as const;

/** CSR-2309 — every browser call to a SAME-ORIGIN AMS endpoint goes through here.
 *  Never an external URL: this forces `credentials: "include"`, which a third-party
 *  CORS policy (a GCS signed upload URL, say) rejects outright. It belongs here because a
 *  401 from this server only ever means the session is gone: the API has just two,
 *  `isAuthenticated` and the `/api/external/*` key guard the browser never calls,
 *  and the COI-service proxy answers 503 rather than relay a service auth failure.
 *  Retracting the cached user is what makes the app notice — it flips
 *  `useAuth().isAuthenticated` false so AuthenticatedRouter swaps in <Login />,
 *  instead of leaving the page to refetch a dead session for another 5 minutes.
 *  Every other status is handed back untouched for the caller to report its own way.
 */
export async function apiFetch(url: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(url, { credentials: "include", ...init });
  if (res.status === 401) {
    queryClient.setQueryData(AUTH_USER_QUERY_KEY, null);
    throw new ApiError(401, "401: session expired");
  }
  return res;
}
